import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { db, storage } from "../firebase";
import { useAuth } from "../AuthProvider";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  setDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  getBusinessTypeLabel,
  summarizeStorePackages,
} from "../utils/storePackages";

const UI_CURRENCY = "LKR";
const fmt = (n) => `${UI_CURRENCY} ${Number(n || 0).toLocaleString("en-LK")}`;
const n = (value) => Number(value || 0);
const RENEWAL_WINDOW_DAYS = 3;
const RENEWAL_WINDOW_MS = RENEWAL_WINDOW_DAYS * 24 * 60 * 60 * 1000;

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function resolveInstructorReferralLink(userDoc = {}) {
  const affiliateUid =
    userDoc?.referredByUid ||
    userDoc?.sponsorUid ||
    userDoc?.affiliateUid ||
    userDoc?.affiliateReferrerUid ||
    null;
  const affiliateReferralCode =
    userDoc?.referredByReferralCode ||
    userDoc?.referralCode ||
    userDoc?.referredBy ||
    userDoc?.refCode ||
    null;
  const hasReferralLink = Boolean(affiliateUid || affiliateReferralCode);
  return {
    affiliateUid,
    affiliateReferralCode,
    referralKind:
      userDoc?.referralKind || (hasReferralLink ? "instructor" : null),
    hasReferralLink,
  };
}

function getLevelAmounts(pkg = {}, kind = "first") {
  if (kind === "renewal") {
    return {
      1: n(pkg.renewalLevel1Amount ?? pkg.affiliateRenewalPaymentAmount),
      2: n(pkg.renewalLevel2Amount),
      3: n(pkg.renewalLevel3Amount),
    };
  }

  return {
    1: n(pkg.firstSaleLevel1Amount ?? pkg.affiliateFirstPaymentAmount),
    2: n(pkg.firstSaleLevel2Amount),
    3: n(pkg.firstSaleLevel3Amount),
  };
}

function getDeveloperAmount(pkg = {}, kind = "first") {
  if (kind === "renewal") {
    return n(pkg.renewalDeveloperAmount ?? pkg.developerAmount);
  }

  return n(pkg.developerAmount);
}

async function copyText(value = "") {
  if (!value) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(String(value));
      return true;
    }
  } catch (_) {}

  try {
    const temp = document.createElement("textarea");
    temp.value = String(value);
    temp.setAttribute("readonly", "");
    temp.style.position = "absolute";
    temp.style.left = "-9999px";
    document.body.appendChild(temp);
    temp.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(temp);
    return ok;
  } catch (_) {
    return false;
  }
}

const PACKAGE_GUIDE = [
  "Pick a package that matches how many courses, products, and services you want to publish.",
  "Transfer the package amount to one of the admin bank accounts shown below.",
  "Upload your payment slip once. Admin will review it and activate your package.",
  "After approval, you can set up your public store in a few quick steps and start listing.",
];

export function StorePackagesPanel({
  embedded = false,
  title = "Store Packages",
  subtitle = "Buy a package to activate your store and unlock posting limits.",
}) {
  const { user } = useAuth();
  const location = useLocation();
  const expiredRedirected =
    new URLSearchParams(location.search).get("expired") === "1";

  const [packages, setPackages] = useState([]);
  const [myDoc, setMyDoc] = useState(null);
  const [orders, setOrders] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [selected, setSelected] = useState(null);
  const [slip, setSlip] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [detailsPkg, setDetailsPkg] = useState(null);
  const [copiedField, setCopiedField] = useState("");

  useEffect(() => {
    const q1 = query(
      collection(db, "instructor_packages"),
      where("isActive", "==", true),
      orderBy("price", "asc"),
    );
    return onSnapshot(q1, (snap) => {
      setPackages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  useEffect(() => {
    const q1 = query(
      collection(db, "bank_accounts"),
      where("isActive", "==", true),
    );
    return onSnapshot(
      q1,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort(
            (a, b) =>
              Number(a.displayOrder ?? 100) - Number(b.displayOrder ?? 100),
          );
        setBankAccounts(rows);
      },
      (error) => {
        console.error("[store-packages] bank account fetch failed", error);
        setBankAccounts([]);
      },
    );
  }, []);

  useEffect(() => {
    if (!user?.uid) return;
    return onSnapshot(doc(db, "users", user.uid), (s) => {
      setMyDoc(s.exists() ? s.data() : null);
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const q2 = query(
      collection(db, "instructor_package_orders"),
      where("instructorId", "==", user.uid),
      orderBy("createdAt", "desc"),
    );
    return onSnapshot(q2, (snap) => {
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [user?.uid]);

  const packageState = useMemo(
    () => summarizeStorePackages(myDoc || {}),
    [myDoc],
  );
  const activePkg = packageState.hasActivePackage ? packageState.summary : null;
  const activePackageExpiryMs = useMemo(
    () => toMillis(activePkg?.expiresAt),
    [activePkg?.expiresAt],
  );
  const activePackageRemainingMs = useMemo(
    () => Math.max(0, activePackageExpiryMs - Date.now()),
    [activePackageExpiryMs],
  );
  const daysUntilExpiry = useMemo(
    () =>
      activePackageExpiryMs
        ? Math.ceil(activePackageRemainingMs / (24 * 60 * 60 * 1000))
        : 0,
    [activePackageExpiryMs, activePackageRemainingMs],
  );
  const renewalWindowOpen = useMemo(() => {
    if (!packageState.hasActivePackage) return true;
    if (!activePackageExpiryMs) return false;
    return activePackageRemainingMs <= RENEWAL_WINDOW_MS;
  }, [
    packageState.hasActivePackage,
    activePackageExpiryMs,
    activePackageRemainingMs,
  ]);

  const pendingOrder = useMemo(
    () =>
      orders.find((order) =>
        ["pending", "submitted", "initiated"].includes(
          String(order.status || "").toLowerCase(),
        ),
      ) || null,
    [orders],
  );
  const packageBuyingLocked =
    packageState.hasActivePackage && !renewalWindowOpen && !pendingOrder;
  const selectedPkg = useMemo(
    () => packages.find((p) => p.id === selected) || null,
    [packages, selected],
  );

  const canSubmit = !!selected && !!slip && !submitting;

  async function handleCopy(label, value, bankId) {
    const ok = await copyText(value);
    if (ok) {
      setErr("");
      setMsg(`${label} copied to clipboard.`);
      setCopiedField(`${bankId}:${label}`);
      window.setTimeout(() => {
        setCopiedField((current) =>
          current === `${bankId}:${label}` ? "" : current,
        );
      }, 1800);
      return;
    }

    setMsg("");
    setErr(`Could not copy ${label.toLowerCase()}. Please copy it manually.`);
  }

  async function submit() {
    if (!user?.uid) return;
    setSubmitting(true);
    setMsg("");
    setErr("");

    try {
      const pkg = packages.find((p) => p.id === selected);
      if (!pkg) return setErr("Please select a package.");
      if (!slip) return setErr("Please upload your payment slip.");
      const isFirstPackagePayment =
        !orders.some((o) => o.status === "approved") &&
        !packageState.hasAnyPackage;
      const referralLink = resolveInstructorReferralLink(myDoc || {});

      const orderRef = doc(collection(db, "instructor_package_orders"));

      // 1) upload slip
      const ext = (slip.name || "").split(".").pop() || "jpg";
      const path = `uploads/slips/${user.uid}/${orderRef.id}/slip.${ext}`;
      const r = ref(storage, path);
      await uploadBytes(r, slip);
      const slipUrl = await getDownloadURL(r);

      // 2) create order with slip in the initial write so instructor rules only need "create"
      await setDoc(orderRef, {
        instructorId: user.uid,
        instructorSnapshot: {
          name:
            myDoc?.fullName ||
            myDoc?.displayName ||
            user.displayName ||
            user.email ||
            user.uid,
          email: myDoc?.email || user.email || "",
        },
        instructorName:
          myDoc?.fullName ||
          myDoc?.displayName ||
          user.displayName ||
          user.email ||
          user.uid,
        instructorEmail: myDoc?.email || user.email || "",
        packageId: pkg.id,
        affiliateReferrerUid: referralLink.affiliateUid || null,
        affiliateUid: referralLink.affiliateUid || null,
        referrerUserId: referralLink.affiliateUid || null,
        sponsorUid: referralLink.affiliateUid || null,
        affiliateReferralCode: referralLink.affiliateReferralCode || null,
        referralSnapshot: {
          affiliateUid: referralLink.affiliateUid,
          affiliateReferralCode: referralLink.affiliateReferralCode,
          referralKind: referralLink.referralKind,
        },
        packageSnapshot: {
          name: pkg.name || "",
          price: n(pkg.price),
          currency: pkg.currency || UI_CURRENCY,
          durationDays: n(pkg.durationDays || 30),

          courseLimit: n(pkg.courseLimit),
          productLimit: n(pkg.productLimit),
          serviceLimit: n(pkg.serviceLimit),
          firstSaleLevel1Amount: n(
            pkg.firstSaleLevel1Amount ?? pkg.affiliateFirstPaymentAmount,
          ),
          firstSaleLevel2Amount: n(pkg.firstSaleLevel2Amount),
          firstSaleLevel3Amount: n(pkg.firstSaleLevel3Amount),
          renewalLevel1Amount: n(
            pkg.renewalLevel1Amount ?? pkg.affiliateRenewalPaymentAmount,
          ),
          renewalLevel2Amount: n(pkg.renewalLevel2Amount),
          renewalLevel3Amount: n(pkg.renewalLevel3Amount),
          affiliateFirstPaymentAmount: n(
            pkg.firstSaleLevel1Amount ?? pkg.affiliateFirstPaymentAmount,
          ),
          affiliateRenewalPaymentAmount: n(
            pkg.renewalLevel1Amount ?? pkg.affiliateRenewalPaymentAmount,
          ),
          developerAmount: getDeveloperAmount(pkg, "first"),
          renewalDeveloperAmount: getDeveloperAmount(pkg, "renewal"),
        },
        payoutSnapshot: {
          firstSaleLevelAmounts: getLevelAmounts(pkg, "first"),
          renewalLevelAmounts: getLevelAmounts(pkg, "renewal"),
          affiliateFirstPaymentAmount: n(
            pkg.firstSaleLevel1Amount ?? pkg.affiliateFirstPaymentAmount,
          ),
          affiliateRenewalPaymentAmount: n(
            pkg.renewalLevel1Amount ?? pkg.affiliateRenewalPaymentAmount,
          ),
          developerAmount: getDeveloperAmount(pkg, "first"),
          renewalDeveloperAmount: getDeveloperAmount(pkg, "renewal"),
          firstSaleDeveloperAmount: getDeveloperAmount(pkg, "first"),
          affiliatePayoutApplied: n(
            isFirstPackagePayment
              ? pkg.firstSaleLevel1Amount ?? pkg.affiliateFirstPaymentAmount
              : pkg.renewalLevel1Amount ?? pkg.affiliateRenewalPaymentAmount,
          ),
          developerPayoutApplied: getDeveloperAmount(
            pkg,
            isFirstPackagePayment ? "first" : "renewal",
          ),
          developerPayoutType: isFirstPackagePayment ? "first_payment" : "renewal",
          affiliatePayoutType: isFirstPackagePayment
            ? "first_payment"
            : "renewal",
        },
        status: "pending",
        createdAt: serverTimestamp(),
        slipUrl,
        updatedAt: serverTimestamp(),
      });

      setMsg("Slip submitted. Admin will review and activate your package.");
      setSlip(null);
      setSelected(null);
    } catch (e) {
      console.error(e);
      setErr("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className={`${embedded ? "space-y-6" : "max-w-6xl mx-auto px-4 py-8 space-y-6"}`}
    >
      <div>
        <h1 className="text-2xl font-bold">{title}</h1>
        <p className="text-gray-600 mt-1">{subtitle}</p>
      </div>

      {activePkg ? (
        <div className="p-4 rounded-2xl border bg-green-50 border-green-200">
          <div className="font-semibold">
            Current Package: {activePkg.name || activePkg.packageId}
          </div>
          <div className="text-sm text-gray-700 mt-1">
            Limits → Course: {Number(activePkg.courseLimit || 0) || "∞"} •
            Product: {Number(activePkg.productLimit || 0) || "∞"} • Service:{" "}
            {Number(activePkg.serviceLimit || 0) || "∞"}
          </div>
          <div className="mt-1 text-xs font-semibold text-gray-700">
            Store mode: {getBusinessTypeLabel(myDoc?.storeBusinessType || "")}
          </div>
          {Number(activePkg.totalPackages || 0) > 1 && (
            <div className="mt-1 text-xs text-gray-600">
              Combined from {activePkg.totalPackages} active packages.
            </div>
          )}
          {activePackageExpiryMs ? (
            <div className="mt-1 text-xs font-medium text-gray-700">
              Access ends on{" "}
              {new Date(activePackageExpiryMs).toLocaleDateString()}.
              {renewalWindowOpen
                ? ` Renewal is now open.`
                : ` Renewal opens during the last ${RENEWAL_WINDOW_DAYS} days.`}
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-3">
            <Link
              to="/dashboard"
              className="rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
            >
              Open Dashboard
            </Link>
            <Link
              to="/store"
              className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-white/70"
            >
              Edit Store
            </Link>
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-2xl border bg-blue-50 border-blue-200 text-blue-800">
          No active package yet. Choose one below and submit your slip.
        </div>
      )}

      {expiredRedirected && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
          <div className="font-semibold">Your subscription has finished.</div>
          <div className="mt-1 text-sm">
            Buy a package again and wait for admin approval to unlock the rest
            of the instructor portal.
          </div>
        </div>
      )}

      {(msg || err) && (
        <div
          className={`p-3 rounded-xl border ${err ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-green-50 border-green-200 text-green-700"}`}
        >
          {err || msg}
        </div>
      )}

      {pendingOrder ? (
        <div className="rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold uppercase tracking-wide text-amber-700">
                Pending Approval
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                Your package submission is under review
              </div>
              <div className="mt-2 max-w-2xl text-sm text-gray-600">
                You already submitted{" "}
                <span className="font-semibold">
                  {pendingOrder.packageSnapshot?.name || pendingOrder.packageId}
                </span>
                . Please wait until admin approves it before submitting another
                package purchase.
              </div>
            </div>
            {pendingOrder.slipUrl ? (
              <a
                href={pendingOrder.slipUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-white"
              >
                View Uploaded Slip
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      {packageBuyingLocked ? (
        <div className="rounded-3xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold uppercase tracking-wide text-blue-700">
                Renewal Locked
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                You can buy your next package during the last{" "}
                {RENEWAL_WINDOW_DAYS} days of this package
              </div>
              <div className="mt-2 max-w-2xl text-sm text-gray-600">
                Your current package is still active. New package purchases
                unlock automatically when only {RENEWAL_WINDOW_DAYS} days or
                less remain on your current access period.
              </div>
            </div>
            <div className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-right shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">
                Days left
              </div>
              <div className="mt-1 text-3xl font-black text-gray-900">
                {daysUntilExpiry}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {activePackageExpiryMs
                  ? `Renewal opens on ${new Date(
                      activePackageExpiryMs - RENEWAL_WINDOW_MS,
                    ).toLocaleDateString()}.`
                  : "Expiry date unavailable."}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {!pendingOrder && !packageBuyingLocked ? (
        <>
          {activePkg ? (
            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-blue-900">
              <div className="font-semibold">
                Renewal window is open for your next package
              </div>
              <div className="mt-1 text-sm">
                Your current package is close to expiry, so you can now choose
                the next package for the same account.
              </div>
            </div>
          ) : null}

          <div className="grid gap-5 lg:grid-cols-4 md:grid-cols-2">
            {packages.map((p, index) => {
              const picked = selected === p.id;
              const accents = [
                "from-fuchsia-500 via-violet-500 to-indigo-600",
                "from-amber-400 via-orange-400 to-rose-500",
                "from-sky-400 via-cyan-400 to-blue-600",
                "from-red-500 via-pink-500 to-orange-500",
              ];
              return (
                <div
                  key={p.id}
                  className={`rounded-[30px] p-3 shadow-lg transition-all duration-200 ${
                    picked
                      ? "scale-[1.03] ring-4 ring-amber-300 shadow-[0_20px_55px_rgba(245,158,11,0.35)]"
                      : "opacity-95 hover:scale-[1.01] hover:opacity-100"
                  } bg-gradient-to-br ${accents[index % accents.length]}`}
                >
                  <div
                    className={`flex h-full flex-col rounded-[24px] px-5 py-6 text-white ${
                      picked
                        ? "border border-white/30 bg-[#160f36]"
                        : "bg-[#1b163b]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 rounded-2xl bg-white/10 px-4 py-3 shadow-inner backdrop-blur">
                      <div className="text-sm font-semibold uppercase tracking-[0.2em] text-white/85">
                        {p.name || "Package"}
                      </div>
                      {picked ? (
                        <span className="rounded-full bg-amber-300 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-950">
                          Selected
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-6 text-center text-4xl font-black">
                      {fmt(p.price)}
                    </div>
                    <div className="mt-2 text-center text-sm text-white/80">
                      {p.durationDays ?? 30} day access
                    </div>

                    <div className="mt-6 rounded-3xl bg-white px-5 py-5 text-sm text-slate-700">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <span>Courses</span>
                          <span className="font-semibold text-slate-900">
                            {Number(p.courseLimit || 0) || "Unlimited"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span>Products</span>
                          <span className="font-semibold text-slate-900">
                            {Number(p.productLimit || 0) || "Unlimited"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span>Services</span>
                          <span className="font-semibold text-slate-900">
                            {Number(p.serviceLimit || 0) || "Unlimited"}
                          </span>
                        </div>
                      </div>
                      {p.description ? (
                        <div className="mt-4 border-t border-slate-100 pt-4 text-xs leading-5 text-slate-500">
                          {p.description}
                        </div>
                      ) : null}
                      {picked ? (
                        <div className="mt-4 rounded-2xl bg-amber-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
                          This is the package you will pay for now.
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-6 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setSelected(p.id)}
                        className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                          picked
                            ? "bg-white text-slate-950"
                            : "bg-white/15 text-white hover:bg-white/25"
                        }`}
                      >
                        {picked ? "Selected Package" : "Buy This Package"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDetailsPkg(p)}
                        className="rounded-xl border border-white/20 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
                      >
                        See More
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.1fr,0.9fr]">
            <div className="rounded-2xl border bg-white p-6 shadow-sm">
              <div className="text-lg font-semibold">Admin bank details</div>
              <div className="mt-2 text-sm text-gray-600">
                Transfer the package amount to one of these accounts, then
                upload your slip below.
              </div>
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
                  Amount to pay
                </div>
                <div className="mt-2 text-3xl font-black text-gray-900">
                  {selectedPkg ? fmt(selectedPkg.price) : fmt(0)}
                </div>
                <div className="mt-1 text-sm text-gray-700">
                  {selectedPkg
                    ? `${selectedPkg.name || "Selected package"} payment amount`
                    : "Select a package above to see the exact payment amount."}
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {bankAccounts.length === 0 ? (
                  <div className="rounded-xl border border-dashed p-4 text-sm text-gray-500">
                    No active bank accounts are available right now. Please
                    contact admin.
                  </div>
                ) : (
                  bankAccounts.map((bank) => (
                    <div
                      key={bank.id}
                      className="rounded-2xl border border-gray-200 bg-gray-50 p-4"
                    >
                      <div className="font-semibold text-gray-900">
                        {bank.bankName || "Bank Account"}
                      </div>
                      <div className="mt-2 grid gap-2 text-sm text-gray-700 md:grid-cols-2">
                        {[
                          ["Account Holder", bank.accountHolder || "-"],
                          ["Account Number", bank.accountNumber || "-"],
                          ["Branch", bank.branch || "-"],
                          ["SWIFT", bank.swift || "-"],
                        ].map(([label, value]) => {
                          const copyKey = `${bank.id}:${label}`;
                          const canCopy = value && value !== "-";

                          return (
                            <div
                              key={label}
                              className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2"
                            >
                              <div className="min-w-0">
                                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                                  {label}
                                </div>
                                <div className="truncate font-medium text-gray-900">
                                  {value}
                                </div>
                              </div>
                              <button
                                type="button"
                                disabled={!canCopy}
                                onClick={() => handleCopy(label, value, bank.id)}
                                className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                                  canCopy
                                    ? copiedField === copyKey
                                      ? "bg-green-100 text-green-700"
                                      : "border border-gray-200 bg-gray-100 text-gray-700 hover:bg-gray-200"
                                    : "cursor-not-allowed bg-gray-100 text-gray-400"
                                }`}
                              >
                                {copiedField === copyKey ? "Copied" : "Copy"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      {bank.notes ? (
                        <div className="mt-2 text-xs text-gray-500">
                          {bank.notes}
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-2xl border bg-white p-6 shadow-sm space-y-3">
                <div className="font-semibold">Submit payment slip</div>
                {selectedPkg ? (
                  <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-green-700">
                          Ready to pay
                        </div>
                        <div className="mt-2 text-lg font-bold text-gray-900">
                          {selectedPkg.name || "Selected package"}
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          {selectedPkg.durationDays ?? 30} days • Course{" "}
                          {Number(selectedPkg.courseLimit || 0) || "Unlimited"} •
                          Product{" "}
                          {Number(selectedPkg.productLimit || 0) || "Unlimited"} •
                          Service{" "}
                          {Number(selectedPkg.serviceLimit || 0) || "Unlimited"}
                        </div>
                      </div>
                      <div className="rounded-2xl bg-white px-4 py-3 text-right shadow-sm">
                        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
                          Amount to pay
                        </div>
                        <div className="mt-1 text-2xl font-black text-green-700">
                          {fmt(selectedPkg.price)}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
                    Choose a package above first. The payment amount will appear
                    here before you upload the slip.
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setSlip(e.target.files?.[0] || null)}
                />
                <button
                  className="px-5 py-2 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500"
                  disabled={!canSubmit}
                  onClick={submit}
                >
                  {submitting ? "Submitting…" : "Submit Slip"}
                </button>
              </div>

              <div className="rounded-2xl border bg-white p-6 shadow-sm">
                <div className="text-lg font-semibold">
                  What happens next?
                </div>
                <div className="mt-3 space-y-3 text-sm text-gray-600">
                  {PACKAGE_GUIDE.map((line) => (
                    <div key={line} className="flex gap-3">
                      <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 font-semibold text-blue-700">
                        {PACKAGE_GUIDE.indexOf(line) + 1}
                      </span>
                      <span>{line}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <div className="bg-white rounded-2xl shadow p-6">
        <div className="font-semibold mb-3">My submissions</div>
        {orders.length === 0 ? (
          <div className="text-sm text-gray-500">No submissions yet.</div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div
                key={o.id}
                className="border rounded-xl p-3 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <div className="font-semibold truncate">
                    {o.packageSnapshot?.name || o.packageId}
                  </div>
                  <div className="text-xs text-gray-500">
                    Status: <span className="font-semibold">{o.status}</span>
                    {o.createdAt?.toDate
                      ? ` • ${o.createdAt.toDate().toLocaleString()}`
                      : ""}
                  </div>
                </div>
                {o.slipUrl ? (
                  <a
                    className="text-sm text-blue-600 hover:underline"
                    href={o.slipUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View slip
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {detailsPkg && (
        <div className="fixed inset-0 z-[70] overflow-y-auto bg-black/50 p-4">
          <div className="mx-auto my-6 max-w-3xl rounded-3xl bg-white shadow-2xl max-h-[calc(100vh-3rem)] overflow-hidden">
            <div className="flex items-center justify-between border-b px-6 py-5">
              <div>
                <div className="text-sm font-semibold uppercase tracking-wide text-blue-700">
                  Package details
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {detailsPkg.name}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetailsPkg(null)}
                className="text-3xl leading-none text-gray-400 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="grid max-h-[calc(100vh-9rem)] gap-6 overflow-y-auto px-6 py-6 lg:grid-cols-[0.9fr,1.1fr]">
              <div className="rounded-3xl bg-gradient-to-br from-blue-50 via-white to-fuchsia-50 p-5">
                <div className="text-4xl font-black text-gray-900">
                  {fmt(detailsPkg.price)}
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  {detailsPkg.durationDays ?? 30} day package
                </div>
                <div className="mt-5 space-y-2 text-sm text-gray-700">
                  <div>
                    Courses:{" "}
                    <span className="font-semibold">
                      {Number(detailsPkg.courseLimit || 0) || "Unlimited"}
                    </span>
                  </div>
                  <div>
                    Products:{" "}
                    <span className="font-semibold">
                      {Number(detailsPkg.productLimit || 0) || "Unlimited"}
                    </span>
                  </div>
                  <div>
                    Services:{" "}
                    <span className="font-semibold">
                      {Number(detailsPkg.serviceLimit || 0) || "Unlimited"}
                    </span>
                  </div>
                </div>
                <div className="mt-5 rounded-2xl border border-white/70 bg-white/80 p-4 text-sm text-gray-700">
                  {detailsPkg.description ||
                    "Great for instructors who want to launch a clean public store, add listings, and start accepting direct payments quickly."}
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <div className="text-lg font-semibold text-gray-900">
                    How your store will work
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    After approval, your dashboard becomes a quick store setup
                    flow. You can add a store name, bio, cover image, payment
                    details, and then start publishing courses, products, or
                    services.
                  </div>
                </div>

                <div className="rounded-3xl border bg-gray-50 p-5">
                  <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                    Simple user manual
                  </div>
                  <div className="mt-3 space-y-3 text-sm text-gray-700">
                    <div>1. Choose this package and pay using the admin bank details.</div>
                    <div>2. Upload the slip once and wait for approval.</div>
                    <div>3. After approval, complete your store setup in a few quick steps.</div>
                    <div>4. Add listings and share your public store link with buyers.</div>
                  </div>
                </div>

                <div className="rounded-3xl border border-blue-100 bg-blue-50 p-5">
                  <div className="text-sm font-semibold uppercase tracking-wide text-blue-700">
                    Store preview
                  </div>
                  <div className="mt-3 overflow-hidden rounded-2xl border bg-white shadow-sm">
                    <div className="h-28 bg-gradient-to-r from-slate-900 via-indigo-700 to-fuchsia-600" />
                    <div className="px-4 pb-4">
                      <div className="-mt-8 h-16 w-16 rounded-full border-4 border-white bg-slate-200" />
                      <div className="mt-3 text-lg font-bold text-gray-900">
                        Your Store Name
                      </div>
                      <div className="text-sm text-gray-600">
                        Add a short bio, upload your images, and make the page
                        feel like your own brand.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setDetailsPkg(null)}
                    className="rounded-xl border px-4 py-2 font-semibold hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(detailsPkg.id);
                      setDetailsPkg(null);
                    }}
                    className="rounded-xl bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
                  >
                    Select This Package
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function StorePackages() {
  return <StorePackagesPanel />;
}
